import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "@/lib/quotationApi";
import FloatingSaveBar from "@/components/FloatingSaveBar";
import TierRow from "@/components/admin/tiers/TierRow";
import { validateTierSettings } from "@/components/admin/tiers/tierValidation";

export default function TierSettings() {
  const [tiers, setTiers] = useState([]);
  const [modularMult, setModularMult] = useState("2.5");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finishes, setFinishes] = useState([]);
  // Shape: { [tier_id]: { [finish_id]: "800" | "" } }
  // Empty string = "leave at base price" (no override).
  const [tierFinishPrices, setTierFinishPrices] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [tr, fr] = await Promise.all([
        api.get("/settings/tiers-v2"),
        api.get("/finishes"),
      ]);
      const loadedTiers = tr.data.tiers || [];
      const loadedFinishes = fr.data || [];
      setTiers(loadedTiers);
      setModularMult(String(tr.data.modular_multiplier ?? 2.5));
      setFinishes(loadedFinishes);
      // Hydrate tier_prices matrix from finishes
      const matrix = {};
      for (const t of loadedTiers) {
        if (t.is_base) continue;
        matrix[t.id] = {};
        for (const f of loadedFinishes) {
          const v = f.tier_prices?.[t.id];
          matrix[t.id][f.id] = v != null ? String(v) : "";
        }
      }
      setTierFinishPrices(matrix);
    } catch {
      toast.error("Failed to load tier settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateTier = (idx, patch) => {
    setTiers((list) => list.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const moveTier = (idx, delta) => {
    setTiers((list) => {
      const copy = [...list];
      const nxt = idx + delta;
      if (nxt < 0 || nxt >= copy.length) return copy;
      [copy[idx], copy[nxt]] = [copy[nxt], copy[idx]];
      return copy.map((t, i) => ({ ...t, order: i }));
    });
  };

  const removeTier = (idx) => {
    setTiers((list) => {
      if (list[idx]?.is_base) {
        toast.error("The base tier (Luxury anchor) cannot be removed");
        return list;
      }
      return list.filter((_, i) => i !== idx).map((t, i) => ({ ...t, order: i }));
    });
  };

  const addTier = () => {
    const newId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setTiers((list) => [
      ...list,
      {
        id: newId,
        name: `Tier ${list.length + 1}`,
        description: "",
        color: "#9AB8D8",
        finish_discount: 0,
        hardware_discount: 0,
        modular_multiplier: null,
        is_base: false,
        order: list.length,
      },
    ]);
    // Seed empty price map for the new tier so rendering doesn't crash.
    setTierFinishPrices((prev) => ({
      ...prev,
      [newId]: Object.fromEntries(finishes.map((f) => [f.id, ""])),
    }));
  };

  const setFinishPrice = (tierId, finishId, value) =>
    setTierFinishPrices((prev) => ({
      ...prev,
      [tierId]: { ...(prev[tierId] || {}), [finishId]: value },
    }));

  const fillAllPrices = (tierId, pct) => {
    const factor = 1 - pct / 100;
    setTierFinishPrices((prev) => {
      const next = { ...(prev[tierId] || {}) };
      for (const f of finishes) {
        next[f.id] = String(Math.round((parseFloat(f.price_per_sqft) || 0) * factor));
      }
      return { ...prev, [tierId]: next };
    });
  };

  const clearAllPrices = (tierId) =>
    setTierFinishPrices((prev) => {
      const next = { ...(prev[tierId] || {}) };
      for (const f of finishes) next[f.id] = "";
      return { ...prev, [tierId]: next };
    });

  const save = async () => {
    const error = validateTierSettings(tiers, tierFinishPrices, finishes, modularMult);
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tiers: tiers.map((t, i) => ({
          id: t.id?.startsWith("tmp_") ? undefined : t.id,
          name: String(t.name || "").trim(),
          description: String(t.description || "").trim(),
          color: t.color || "#C5A880",
          // No more % fallback for finishes. Blank per-finish inputs = base price.
          // Quick prefill still writes absolute rupee values into each finish's
          // tier_prices map; the tier-level finish_discount is intentionally
          // wiped here so empty inputs mirror the base-tier price exactly.
          finish_discount: 0,
          // Hardware discount is no longer exposed in the UI — always force 0
          // so accessories/hardware stay at full master price for every tier.
          hardware_discount: 0,
          modular_multiplier:
            t.modular_multiplier == null || t.modular_multiplier === ""
              ? null
              : parseFloat(t.modular_multiplier),
          is_base: Boolean(t.is_base),
          order: i,
        })),
        modular_multiplier: parseFloat(modularMult),
      };
      const r = await api.put(`/settings/tiers-v2`, payload);
      const savedTiers = r.data.tiers || [];
      setTiers(savedTiers);
      setModularMult(String(r.data.modular_multiplier ?? 2.5));

      // Now persist per-finish tier prices. For each finish, build the new
      // tier_prices map (only include tiers where admin typed a positive number)
      // and PUT /finishes/{id} only when the map changed.
      // Rebuild matrix keyed off the SAVED tier ids (in case new tiers were created).
      const idRemap = {};
      tiers.forEach((t, i) => {
        if (t.id?.startsWith("tmp_")) {
          const saved = savedTiers[i];
          if (saved) idRemap[t.id] = saved.id;
        }
      });
      const finishUpdates = [];
      for (const f of finishes) {
        const next = {};
        for (const t of savedTiers) {
          if (t.is_base) continue;
          const srcId = Object.keys(idRemap).find((k) => idRemap[k] === t.id) || t.id;
          const raw = (tierFinishPrices[srcId] || tierFinishPrices[t.id] || {})[f.id];
          if (raw !== "" && raw != null) {
            const num = parseFloat(raw);
            if (Number.isFinite(num) && num >= 0) next[t.id] = num;
          }
        }
        const prev = f.tier_prices || {};
        const keysChanged =
          Object.keys(next).length !== Object.keys(prev).length ||
          Object.keys(next).some((k) => Number(prev[k]) !== next[k]);
        if (keysChanged) {
          finishUpdates.push(api.put(`/finishes/${f.id}`, { tier_prices: next }));
        }
      }
      if (finishUpdates.length > 0) {
        await Promise.all(finishUpdates);
      }

      toast.success(
        finishUpdates.length > 0
          ? `Tiers & ${finishUpdates.length} finish price${finishUpdates.length === 1 ? "" : "s"} updated`
          : "Tiers updated",
      );
      // Reload to pick up canonical finish ids and cleaned matrix
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-white/50 text-sm">Loading…</div>;

  return (
    <div className="card-luxe space-y-5" data-testid="tier-settings">
      <div>
        <div className="overline text-[#C5A880] mb-2">Pricing Tiers</div>
        <p className="text-white/50 text-sm">
          Manage the tiers offered on quotations. The <span className="text-[#C5A880]">base tier</span> (marked with a crown) uses the master finish prices. For every other tier, type the actual rupee rate per sqft for each finish — leave blank to fall back to the base price.
        </p>
      </div>

      <div className="space-y-3" data-testid="tier-list">
        {tiers.map((t, idx) => (
          <TierRow
            key={t.id || idx}
            t={t}
            idx={idx}
            count={tiers.length}
            finishes={finishes}
            modularMult={modularMult}
            prices={tierFinishPrices[t.id]}
            onPatch={(patch) => updateTier(idx, patch)}
            onMove={(delta) => moveTier(idx, delta)}
            onRemove={() => removeTier(idx)}
            onSetFinishPrice={setFinishPrice}
            onFillAll={fillAllPrices}
            onClearAll={clearAllPrices}
          />
        ))}
      </div>

      <button
        onClick={addTier}
        className="btn-ghost w-full flex items-center justify-center gap-2"
        data-testid="tier-add-btn"
      >
        <Plus size={16} /> Add tier
      </button>

      <div className="rounded-xl border border-white/10 p-3">
        <label className="label-luxe">Default modular multiplier</label>
        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <input
            type="number"
            step="0.1"
            inputMode="decimal"
            className="input-luxe"
            value={modularMult}
            onChange={(e) => setModularMult(e.target.value)}
            placeholder="2.5"
            data-testid="tier-default-mm-input"
          />
          <div className="rounded-lg border border-[#C5A880]/30 bg-[#C5A880]/5 px-3 py-2 text-[11px] text-[#C5A880] whitespace-nowrap">
            ×{parseFloat(modularMult) || 0} finish rate
          </div>
        </div>
        <p className="text-[11px] text-white/40 mt-1.5">
          Applies to finish rates whenever a measurement is marked <span className="text-white/70">Modular</span>. Each tier can override this individually.
        </p>
      </div>

      <p className="text-[11px] text-white/40">
        Tip: Changes apply to <span className="text-white/60">new</span> quotations. Re-save an existing quote to refresh its tier totals.
      </p>

      <FloatingSaveBar
        onSave={save}
        saving={saving}
        label="Save tiers"
        savingLabel="Saving…"
        testId="tier-save-btn"
      />
    </div>
  );
}
